/**
 * @file store/theme.ts
 * @description 主题状态管理模块，管理应用的颜色主题配置
 */

import utils from '@/utils';
import { defineStore } from 'pinia';

/**
 * 主题状态 Store
 * @description 管理应用的主题色彩配置，包括 Element Plus 主题色和头部样式
 */
export const useThemeStore = defineStore('theme', {
    /**
     * 状态定义
     * @property primary - 主色调
     * @property success - 成功色
     * @property warning - 警告色
     * @property danger - 危险色
     * @property info - 信息色
     * @property headerBgColor - 头部背景色
     * @property headerTextColor - 头部文字色
     */
    state: () => {
        return {
            primary: '',
            success: '',
            warning: '',
            danger: '',
            info: '',
            headerBgColor: '#242f42', // 头部默认背景色
            headerTextColor: '#fff', // 头部默认文字色
        };
    },
    getters: {},
    actions: {
        /**
         * 初始化主题
         * @description 从 localStorage 恢复用户之前的主题设置
         */
        initTheme() {
            // 遍历所有主题色类型，从 localStorage 加载
            ['primary', 'success', 'warning', 'danger', 'info'].forEach((type) => {
                const color = localStorage.getItem(`theme-${type}`) || '';
                if (color) {
                    this.setPropertyColor(color, type); // 设置主题色
                }
            });
            // 恢复头部背景色
            const headerBgColor = localStorage.getItem('header-bg-color');
            headerBgColor && this.setHeaderBgColor(headerBgColor);
            // 恢复头部文字色
            const headerTextColor = localStorage.getItem('header-text-color');
            headerTextColor && this.setHeaderTextColor(headerTextColor);
        },

        /**
         * 重置主题到默认值
         */
        resetTheme() {
            ['primary', 'success', 'warning', 'danger', 'info'].forEach((type) => {
                this.setPropertyColor('', type); // 清空主题色，恢复默认
            });
        },

        /**
         * 设置主题色
         * @param color - 颜色值（十六进制格式）
         * @param type - 颜色类型（primary/success/warning/danger/info）
         */
        setPropertyColor(color: string, type: string = 'primary') {
            // @ts-ignore
            this[type] = color;
            // 设置 CSS 变量，影响 Element Plus 主题色
            utils.setProperty(`--el-color-${type}`, color);
            localStorage.setItem(`theme-${type}`, color); // 持久化到 localStorage
            this.setThemeLight(type); // 生成对应的淡色系列
        },

        /**
         * 生成主题色的淡色变体
         * @description 根据主色生成不同透明度的颜色变体，用于 Element Plus 组件
         * @param type - 颜色类型
         */
        setThemeLight(type: string = 'primary') {
            // 生成 light-3, light-5, light-7, light-8, light-9 的颜色变体
            [3, 5, 7, 8, 9].forEach((v) => {
                // @ts-ignore
                utils.setProperty(`--el-color-${type}-light-${v}`, utils.mix('#ffffff', this[type], v / 10));
            });
            // @ts-ignore
            // 生成 dark-2 的颜色变体
            utils.setProperty(`--el-color-${type}-dark-2`, utils.mix('#ffffff', this[type], 0.2));
        },

        /**
         * 设置头部背景色
         * @param color - 背景色值
         */
        setHeaderBgColor(color: string) {
            this.headerBgColor = color;
            utils.setProperty('--header-bg-color', color);
            localStorage.setItem(`header-bg-color`, color);
        },

        /**
         * 设置头部文字色
         * @param color - 文字色值
         */
        setHeaderTextColor(color: string) {
            this.headerTextColor = color;
            utils.setProperty('--header-text-color', color);
            localStorage.setItem(`header-text-color`, color);
        }
    }
});
